import { ApolloError } from '@apollo/client';
import { formatCurrency, formatNumber } from 'utils/format';
import { PriceRequest, PriceRequestAction, PriceRequestSide } from 'models/price-request';

/** DD-3697: display helpers for the request pages. No business rules — the backend decides. */

export const PRICE_REQUEST_CHANGED = 'PRICE_REQUEST_CHANGED';

/** DD-3840: the lab's request log is the Price adjustments tab of its customer list; the chain's is /price-requests. */
export const LAB_PRICE_REQUESTS_URL = '/client-directory/price-adjustments';

export const hasAction = (request: Pick<PriceRequest, 'allowedActions'>, action: PriceRequestAction): boolean =>
    request.allowedActions.includes(action);

/** The other party of a request as the viewer sees it (the backend tells the viewer's side). */
export const counterpartOf = (request: Pick<PriceRequest, 'viewerSide' | 'chain' | 'lab'>) =>
    request.viewerSide === PriceRequestSide.CHAIN ? request.lab : request.chain;

/** "+125.00 SEK" / "-40.00 SEK"; empty when there is no delta. */
export const formatSignedCurrency = (value: number | null | undefined, currency: string): string => {
    if (value === null || value === undefined || Number.isNaN(value)) return '';
    const formatted = formatCurrency(Math.abs(value), currency);
    if (value > 0) return `+${formatted}`;
    if (value < 0) return `-${formatted}`;
    return formatted;
};

/** pricePercent arrives as a percentage (5.3 = 5.3 %), rounded by the backend. */
export const formatSignedPercent = (value: number | null | undefined): string => {
    if (value === null || value === undefined || Number.isNaN(value)) return '';
    const formatted = `${formatNumber(Math.abs(value), { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`;
    if (value > 0) return `+${formatted}`;
    if (value < 0) return `-${formatted}`;
    return formatted;
};

const errorCodes = (error: ApolloError): string[] =>
    error.graphQLErrors.flatMap((graphQLError) => {
        const extensions = graphQLError.extensions as
            | { code?: unknown; exception?: { type?: unknown; otherInfo?: { code?: unknown } } }
            | undefined;
        return [
            graphQLError.message,
            extensions?.code,
            extensions?.exception?.type,
            extensions?.exception?.otherInfo?.code,
        ].filter((value): value is string => typeof value === 'string');
    });

/** approvePriceRequest refused because the lab changed its prices after the revision shown. */
export const isPriceRequestChanged = (error: unknown): boolean =>
    error instanceof ApolloError && errorCodes(error).some((code) => code.includes(PRICE_REQUEST_CHANGED));

/** The backend's message for a refused action, or undefined for network failures. */
export const graphQLErrorMessage = (error: unknown): string | undefined =>
    error instanceof ApolloError ? error.graphQLErrors[0]?.message : undefined;

/**
 * The price-requests locale key for a failed mutation: the backend answers with codes
 * (PRICE_REQUEST_LINE_NO_CHANGE …), which get an explained text each; anything else falls
 * back to a generic message rather than showing a raw code.
 */
export const errorMessageKey = (error: unknown): string => {
    if (!(error instanceof ApolloError)) return 'ERROR_ACTION_FAILED';
    if (error.networkError && !error.graphQLErrors.length) return 'ERROR_NETWORK';
    const code = error.graphQLErrors[0]?.message ?? '';
    return /^[A-Z][A-Z_]+$/.test(code) ? `ERROR_CODE_${code}` : 'ERROR_ACTION_FAILED';
};
